import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import s from './adminStyles';
import { authHeaders } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const empty = {
  title: '',
  description: '',
  price: '',
  location: '',
  region: 'Costa Blanca',
  property_type: 'Villa',
  bedrooms: '',
  bathrooms: '',
  surface: '',
  images: '',
  external_url: 'https://example.com',
  featured: false,
};

const PropertyForm = () => {
  const [properties, setProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(empty);

  useEffect(() => { load(); }, []);

  const load = () => {
    fetch(`${API_URL}/api/properties`)
      .then(r => r.json())
      .then(setProperties)
      .catch(console.error);
  };

  const reset = () => { setForm(empty); setEditingItem(null); };

  const startEdit = (p) => {
    setEditingItem(p);
    setForm({ ...p, images: (p.images || []).join(', ') });
    setShowForm(true);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...form,
      price: parseInt(form.price),
      bedrooms: parseInt(form.bedrooms),
      bathrooms: parseInt(form.bathrooms),
      surface: parseInt(form.surface),
      images: form.images.split(',').map(x => x.trim()),
    };
    const url = editingItem
      ? `${API_URL}/api/properties/${editingItem.id}`
      : `${API_URL}/api/properties`;
    const method = editingItem ? 'PUT' : 'POST';
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(data),
    })
      .then(r => r.json())
      .then(() => { load(); reset(); setShowForm(false); })
      .catch(console.error);
  };

  const onDelete = (id) => {
    if (!window.confirm('Weet je zeker dat je deze woning wilt verwijderen?')) return;
    fetch(`${API_URL}/api/properties/${id}`, { method: 'DELETE', headers: authHeaders() })
      .then(load)
      .catch(console.error);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.sectionTitle}>Woningen Beheer</h2>
        <button
          style={s.btnPrimary}
          onClick={() => { setShowForm(!showForm); reset(); }}
          data-testid="add-property-button"
        >
          <Plus size={20} />
          Nieuwe woning
        </button>
      </div>

      {showForm && (
        <form style={s.form} onSubmit={onSubmit} data-testid="property-form">
          <div style={s.formGrid}>
            <div style={s.formGroup}>
              <label style={s.label}>Titel</label>
              <input style={s.input} type="text" value={form.title} onChange={set('title')} required data-testid="property-title-input" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Prijs (€)</label>
              <input style={s.input} type="number" value={form.price} onChange={set('price')} required data-testid="property-price-input" />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Locatie</label>
              <input style={s.input} type="text" value={form.location} onChange={set('location')} required />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Regio</label>
              <select style={s.input} value={form.region} onChange={set('region')} required>
                <option value="Costa Blanca">Costa Blanca</option>
                <option value="Costa Calida">Costa Calida</option>
                <option value="Costa Almeria">Costa Almeria</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Type</label>
              <select style={s.input} value={form.property_type} onChange={set('property_type')} required>
                <option value="Villa">Villa</option>
                <option value="Appartement">Appartement</option>
                <option value="Penthouse">Penthouse</option>
                <option value="Townhouse">Townhouse</option>
              </select>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Slaapkamers</label>
              <input style={s.input} type="number" value={form.bedrooms} onChange={set('bedrooms')} required />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Badkamers</label>
              <input style={s.input} type="number" value={form.bathrooms} onChange={set('bathrooms')} required />
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>Oppervlakte (m²)</label>
              <input style={s.input} type="number" value={form.surface} onChange={set('surface')} required />
            </div>
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Beschrijving</label>
            <textarea style={{ ...s.input, minHeight: '100px' }} value={form.description} onChange={set('description')} required />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Afbeeldingen (URLs, gescheiden door komma's)</label>
            <textarea style={s.input} value={form.images} onChange={set('images')} placeholder="https://example.com/1.jpg, https://example.com/2.jpg" required />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Externe URL</label>
            <input style={s.input} type="url" value={form.external_url} onChange={set('external_url')} required />
          </div>
          <div style={s.checkboxGroup}>
            <input
              type="checkbox"
              id="featured"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              data-testid="property-featured-checkbox"
            />
            <label htmlFor="featured" style={s.checkboxLabel}>Uitgelicht</label>
          </div>
          <div style={s.formActions}>
            <button type="submit" style={s.btnPrimary} data-testid="submit-property-button">
              {editingItem ? 'Bijwerken' : 'Toevoegen'}
            </button>
            <button
              type="button"
              style={s.btnSecondary}
              onClick={() => { setShowForm(false); reset(); }}
            >
              Annuleren
            </button>
          </div>
        </form>
      )}

      <div style={s.table}>
        {properties.map(p => (
          <div key={p.id} style={s.tableRow} data-testid="property-row">
            <div style={s.tableCell}>
              <img src={p.images[0]} alt={p.title} style={s.thumbnail} />
            </div>
            <div style={s.tableCell}>
              <strong>{p.title}</strong>
              <p style={s.cellSubtext}>{p.location}</p>
            </div>
            <div style={s.tableCell}>€{p.price.toLocaleString()}</div>
            <div style={s.tableCell}>{p.region}</div>
            <div style={s.tableCell}>
              <button style={s.iconButton} onClick={() => startEdit(p)} data-testid="edit-property-button">
                <Edit size={18} />
              </button>
              <button
                style={{ ...s.iconButton, ...s.deleteButton }}
                onClick={() => onDelete(p.id)}
                data-testid="delete-property-button"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PropertyForm;
